import { Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { addIcons } from 'ionicons';
import * as icons from 'ionicons/icons';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  NavController,
  IonHeader,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonText,
  IonBadge,
  IonLabel,
  IonImg,
  IonSkeletonText,
  IonTitle,
  MenuController,
  ModalController,
} from '@ionic/angular/standalone';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { UserRolesService } from 'src/app/core/services/auth/user-roles.service';
import { User } from '../../core/models/User';
import { NotificationsService } from 'src/app/core/services/notifications/notifications.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { ProductsService } from 'src/app/core/services/commerce/products.service';
import { CartService } from 'src/app/core/services/commerce/cart.service';
import { ModalService } from 'src/app/core/services/navigation/modal.service';
import { CharitySelectorBottomSheetComponent } from 'src/app/role-user/components/charity-selector-bottom-sheet/charity-selector-bottom-sheet.component';
import { BusinessSelectorBottomSheetComponent } from 'src/app/role-business/components/business-selector-bottom-sheet/business-selector-bottom-sheet.component';
import { GuestUserService } from 'src/app/core/services/user/guest-user.service';
import { Charity } from 'src/app/core/models/Charity';
import { Business } from 'src/app/core/models/Business';
import { environment } from 'src/environments/environment';
import { PortalSwitchService } from 'src/app/core/services/navigation/portal-switch.service';
import { AlertService } from 'src/app/core/services/navigation/alert.service';

const MAIN_TABS: string[] = [
  '/main/home',
  '/main/discover',
  '/main/donate',
  '/main/sunny-drives',
  '/main/commerce',
];

const userTabs: string[] = ['home', 'discover', 'donate', 'sunny-drives', 'commerce'];

@Component({
    selector: 'app-user-role-tabs',
    templateUrl: './user-role-tabs.component.html',
    styleUrls: ['./user-role-tabs.component.scss'],
    imports: [
        IonBadge,
        IonText,
        IonHeader,
        IonToolbar,
        IonButton,
        IonButtons,
        IonIcon,
        IonTabs,
        IonTabBar,
        IonTabButton,
        IonBadge,
        IonLabel,
        IonImg,
        IonSkeletonText,
        IonTitle
    ]
})
export class UserRoleTabsComponent implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private winningProductsRefreshSub!: Subscription;
  private cartRefreshSub!: Subscription;
  private menuController = inject(MenuController);
  private userRolesService = inject(UserRolesService);
  authService = inject(AuthService);
  appName = environment.appName;

  permittedCharities: Charity[] = [];
  permittedBusinesses: Business[] = [];
  rolesLoaded: boolean = false;

  currentTab!: string;
  isMainPage: boolean = true;
  loggedInUser!: User;
  notificationCount: number = 0;
  pendingWonItemsCount: number = 0;
  cartItemCount: number = 0;
  private isNavigating: boolean = false;
  winningBanner: boolean = false;
  imageLoaded: boolean = false;
  pageTitle: string = '';
  loadingUser: boolean = true;
  isSwitchingToCharity: boolean = false;
  isSwitchingToBusiness: boolean = false;

  get pageTitleClass(): string {
    return this.pageTitle && this.pageTitle.length > 14 ? 'page-title page-title-small' : 'page-title';
  }

  get cartBadgeCount(): number {
    return this.pendingWonItemsCount + this.cartItemCount;
  }

  constructor(
    private navController: NavController,
    private router: Router,
    private notificationsService: NotificationsService,
    private tabService: TabService,
    private productsService: ProductsService,
    private cartService: CartService,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private modalCtrl: ModalController,
    private guestUserService: GuestUserService,
    private portalSwitchService: PortalSwitchService,
    private alertService: AlertService,
  ) {
    addIcons({ ...icons });
    this.getLoggedInUserInfo();
    this.subscribeToRoles();

    const currentUrl: string = this.router.url;
    if (currentUrl.startsWith('/main/')) {
      const tabName: string | undefined = currentUrl.split('/')[2]?.split('?')[0];
      if (tabName && userTabs.includes(tabName)) {
        this.currentTab = tabName;
        this.tabService.setLastTab('user', tabName);
      }
    }

    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('user');

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url: string = event.urlAfterRedirects;
        if (!url.startsWith('/main')) return;
        this.isMainPage = !!MAIN_TABS.find(page => page === url);
        
        const state: StateType = this.router.getCurrentNavigation()?.extras.state || {};
        const sourceTab: string | undefined = state['sourceTab'];
        
        if (this.isMainPage) {
          const tabName: string | undefined = url.split('/').pop();
          if (tabName && userTabs.includes(tabName)) {
            this.currentTab = tabName;
            this.tabService.setLastTab('user', tabName);
          }
        } else if (url.startsWith('/main/cart')) {
          // Cart is a stack page (not in MAIN_TABS, so the back button still shows) but it lives
          // under the commerce tab. Old winning-items links redirect here without sourceTab state,
          // so this can't just fall through to the generic sourceTab branch below.
          this.currentTab = 'commerce';
          this.tabService.setLastTab('user', 'commerce');
        } else if (sourceTab) {
          this.currentTab = sourceTab;
          this.tabService.setLastTab('user', sourceTab);
        }
        
        let currentRoute: ActivatedRoute = this.route.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        this.pageTitle = currentRoute.snapshot.data['title'] || '';
        this.tabService.trackNavigation('user', this.currentTab, event.urlAfterRedirects, state);
      }
    });
  }

  getLoggedInUserInfo() {
    this.subscriptions.add(
      this.authService.user$.pipe(
        debounceTime(50) // Prevent race conditions during modal cleanup
      ).subscribe(user => {
        if (user) {
          this.loggedInUser = user;
          this.loadingUser = false;
          this.getNotificationCount(user.id ?? '');
          // No explicit getWinningProducts() here — winningProductsRefresh$ is a replaying
          // BehaviorSubject, so setupWinningProductsRefresh() drives the initial load. Calling both
          // fetched /product-winners twice on every page load. Mirrors setupCartRefresh() below.
          this.setupWinningProductsRefresh(user.id ?? '');
          this.setupCartRefresh();
          if (!user.avatarUrl || user.avatarUrl === 'test') {
            this.loggedInUser.avatarUrl = 'assets/ic-profile-charity.svg';
          }
        } else {
          this.getGuestSession()
          this.loadingUser = false;
        }
      }),
    );
  }

  getGuestSession() {
    this.guestUserService.user$.subscribe(user => {
      if(user && localStorage.getItem('guestSessionId')){
        this.setupWinningProductsRefresh(user.id ?? '');
      }
    })
  }

  getNotificationCount(id: string) {
    this.notificationsService.loadUnreadCountByUserId(Number(id));
    this.subscriptions.add(
      this.notificationsService.userNotificationCount$.subscribe(count => {
        this.notificationCount = count ?? 0;
      }),
    );
  }

  getWinningProducts(id: string) {
    this.subscriptions.add(
      // No status filter (SUN-991): the endpoint defaults to status != PAID, so Overdue (UNPAID)
      // wins still need the winner's action and keep driving the banner + header badge (see
      // pendingWonItemsCount below) alongside PENDING; only PAID/released wins drop off.
      this.productsService.getWinningProductsByUserId(id).subscribe({
        next: winners => {
          this.pendingWonItemsCount = winners.length;
          if(winners.length > 0){
            this.winningBanner = true;
            document.body.classList.add('banner-visible');
          } else {
            this.winningBanner = false;
            document.body.classList.remove('banner-visible');
          }
        }
      })
    )
  }

  setupWinningProductsRefresh(userId: string) {
    if (this.winningProductsRefreshSub) {
      this.winningProductsRefreshSub.unsubscribe();
    }
    
    this.winningProductsRefreshSub = this.productsService.winningProductsRefresh$.subscribe(() => {
      this.getWinningProducts(userId);
    });

    this.subscriptions.add(this.winningProductsRefreshSub);
  }

  getCartItems(): void {
    this.subscriptions.add(
      this.cartService.getCartProducts().subscribe({
        next: products => {
          this.cartItemCount = products.length;
        }
      })
    );
  }

  // cartRefresh$ is a replaying BehaviorSubject, so subscribing here also drives the initial
  // load. Deliberately NOT re-run on every NavigationEnd — only on login (here) and whatever
  // mutates the cart elsewhere calling CartService.refreshCart().
  setupCartRefresh(): void {
    if (this.cartRefreshSub) {
      this.cartRefreshSub.unsubscribe();
    }

    this.cartRefreshSub = this.cartService.cartRefresh$.subscribe(() => {
      this.getCartItems();
    });

    this.subscriptions.add(this.cartRefreshSub);
  }

  navigateToTab(tab: string) {
    if (this.isNavigating) return;

    // Read AuthService's signals directly rather than the debounced user$-derived fields:
    // those resolve on a 50ms timer that regularly beats the cold-load /auth/me round trip,
    // briefly looking like "guest" to an authenticated user (SUN-866).
    if (tab === 'home' && !this.authService.currentUser() && this.authService.hydrated()) {
      this.modalService.showGuestModal('Welcome to your personal giving dashboard!', 'home');
      return;
    }

    this.isNavigating = true;

    if (tab === this.currentTab) {
      // If clicking the same tab, go to root of that tab
      this.tabService.resetTabHistory('user', tab);
      this.navController.navigateRoot(`/main/${tab}`)
        .finally(() => {
          this.isNavigating = false;
        });
    } else {
      this.currentTab = tab;
      this.tabService.setLastTab('user', tab);
      // Restore last position in that tab (matching mobile tab behavior)
      const lastTabPage: { url: string; state: StateType } | null = this.tabService.getLastTabPage(
        'user',
        tab,
      );
      if (lastTabPage) {
        this.navController.navigateRoot(lastTabPage.url, { state: lastTabPage.state || {} })
          .finally(() => {
            this.isNavigating = false;
          });
      } else {
        this.navController.navigateRoot(`/main/${tab}`)
          .finally(() => {
            this.isNavigating = false;
          });
      }
    }
  }

  logInModal(){
    this.modalService.showLoginModal();
  }

  signUpModal(){
    this.modalService.showSignupModal();
  }

  handleTabChange(event: { tab: string }) {
    const page: string = event.tab;
    if (this.isNavigating) return;

    // Same hydration gate as navigateToTab() — ionTabsWillChange also fires on the tabs
    // component's initial activation, i.e. mid-startup on a cold load (SUN-866).
    if (page === 'home' && !this.authService.currentUser() && this.authService.hydrated()) {
      this.modalService.showGuestModal('Welcome to your personal giving dashboard!', 'home');
      return;
    }

    this.isNavigating = true;

    if (userTabs.find(tab => tab === page)) {
      if (page === this.currentTab) {
        this.tabService.resetTabHistory('user', page);
        this.navController.navigateRoot(`/main/${page}`).finally(() => {
          this.isNavigating = false;
        });
      } else {
        this.currentTab = page;
        this.tabService.setLastTab('user', page);
        const lastTabPage: { url: string; state: StateType } | null = this.tabService.getLastTabPage(
          'user',
          this.currentTab,
        );
        if (lastTabPage) {
          this.navController
            .navigateRoot(lastTabPage.url, { state: lastTabPage.state || {} })
            .finally(() => {
              this.isNavigating = false;
            });
        } else {
          this.navController.navigateRoot(`/main/${page}`)
            .finally(() => {
              this.isNavigating = false;
            });
        }
      }
    } else {
      if (!this.currentTab) this.currentTab = this.tabService.getLastTab('user');
      this.isNavigating = false;
    }
  }

  goToCart() {
    this.navController.navigateRoot('/main/cart', { state: { sourceTab: 'commerce' } }).then(() => {
      this.isNavigating = false;
    });
  }

  goToMenuPage() {
    this.menuController.open('app-menu');
  }

  goToNotificationsPage() {
    this.navController.navigateForward(['/main/notifications']).then(r => r);
  }

  goToDonationHistory() {
    this.navController.navigateForward(['/main/history']);
  }

  goToRecurringDonations() {
    const userId: string | undefined = this.loggedInUser?.id;
    if (userId) {
      this.navController.navigateForward(['/main/recurring-donations', userId]);
    }
  }

  back() {
    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('user');
    const backTabPage: { url: string; state: StateType } | null = this.tabService.navigateBack(
      'user',
      this.currentTab,
    );
    if (backTabPage) {
      this.navController.navigateBack(backTabPage.url, { state: backTabPage.state || {} });
    } else {
      this.navController.navigateRoot(`/main/${this.currentTab}`);
    }
  }

  private subscribeToRoles(): void {
    this.subscriptions.add(
      this.userRolesService.rolesLoaded$.subscribe((loaded: boolean) => {
        this.rolesLoaded = loaded;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedCharities$.subscribe((charities: Charity[]) => {
        this.permittedCharities = charities;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedBusinesses$.subscribe((businesses: Business[]) => {
        this.permittedBusinesses = businesses;
      })
    );
  }

  async sidebarSwitchToCharity(): Promise<void> {
    if (this.isSwitchingToCharity) {
      return;
    }

    if (!this.permittedCharities?.length) return;

    if (this.permittedCharities.length > 1) {
      const modal: HTMLIonModalElement = await this.modalCtrl.create({
        component: CharitySelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: {
          permittedCharities: this.permittedCharities,
          currentCharityId: sessionStorage.getItem('currentCharityId') || sessionStorage.getItem('charityId')
        },
        backdropDismiss: true,
      });
      await modal.present();
      return;
    }

    await this.performCharitySidebarSwitch();
  }

  private async performCharitySidebarSwitch(): Promise<void> {
    this.isSwitchingToCharity = true;
    try {
      const switched: boolean = await this.portalSwitchService.switchToCharity(this.permittedCharities[0].id);
      if (!switched) {
        this.alertService.showToast("Couldn't switch portals. Please try again.", 'danger');
      }
    } finally {
      this.isSwitchingToCharity = false;
    }
  }

  async sidebarSwitchToBusiness(): Promise<void> {
    if (this.isSwitchingToBusiness) {
      return;
    }

    if (!this.permittedBusinesses?.length) return;

    if (this.permittedBusinesses.length > 1) {
      const modal: HTMLIonModalElement = await this.modalCtrl.create({
        component: BusinessSelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: {
          permittedBusinesses: this.permittedBusinesses,
          currentBusinessId: null
        },
        backdropDismiss: true,
      });
      await modal.present();
      return;
    }

    await this.performBusinessSidebarSwitch();
  }

  private async performBusinessSidebarSwitch(): Promise<void> {
    this.isSwitchingToBusiness = true;
    try {
      const switched: boolean = await this.portalSwitchService.switchToBusiness(this.permittedBusinesses[0].id);
      if (!switched) {
        this.alertService.showToast("Couldn't switch portals. Please try again.", 'danger');
      }
    } finally {
      this.isSwitchingToBusiness = false;
    }
  }

  async sidebarSignOut(): Promise<void> {
    await this.authService.logout();
    this.navController.navigateRoot('/welcome');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('banner-visible');
    this.subscriptions.unsubscribe();
  }
}
